import {
  ApiAttributionModel,
  ApiChartYAxisKey,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { MoreHoriz } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { SparkLineChart } from "@mui/x-charts";
import IncreaseDecrease from "../components/IncreaseDecrease";
import { StatCardTypography } from "Components/UI/StatCard";
import { AnimatePresence, motion } from "motion/react";
import { IChart, TChartData } from "Interfaces";
import { IFromToChartOutput, useFromToContext } from "Providers/FromToProvider";
import { ReactNode, useMemo, useState } from "react";
import ErrorBoundary from "features/common/components/ErrorBoundary";
import { useAttributionContext } from "Providers/AttributionProvider";
import { useMetricName } from "../hooks/useMetricName";
import {
  percentageTopChangeByMetric,
  replaceAttributionMetricsInChart,
} from "../utils/utils";
import AnalyticsStatCard from "./AnalyticsStatCard";
import useFormatMetric from "../hooks/useFormatMetric";
import AnalyticsCardToolBar from "./AnalyticsCardToolBar";
import { formatNumber } from "Helpers/numbers";

function hasSparklineData(trendPoints: number[]) {
  return trendPoints.length > 0 && trendPoints.some((point) => point !== 0);
}

function StatCardSparkline({
  trendPoints,
  noBg,
}: {
  trendPoints: number[];
  noBg?: boolean;
}) {
  const theme = useTheme();
  const sparkColor = noBg
    ? theme.palette.primary.main
    : "rgba(255,255,255,0.6)";

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: -13,
        left: (theme) => `-${theme.spacing(2)}`,
        right: (theme) => `-${theme.spacing(2)}`,
        height: 52,
        opacity: 0.55,
        pointerEvents: "none",
      }}
    >
      <SparkLineChart
        data={trendPoints}
        curve="natural"
        area
        height={52}
        colors={[sparkColor]}
        margin={{ top: 4, bottom: 0, left: 0, right: 0 }}
        sx={{
          "& .MuiAreaElement-root": { opacity: 0.5 },
        }}
        yAxis={{
          min: Math.min(...trendPoints),
          max: Math.max(...trendPoints),
        }}
      />
    </Box>
  );
}

function StatCardComparison({
  change,
  noBg,
}: {
  change: number;
  noBg?: boolean;
}) {
  const direction = change > 0 ? "up" : change < 0 ? "down" : "same";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ flexWrap: "wrap" }}
    >
      <IncreaseDecrease
        show="icon"
        direction={direction}
        value={change}
        fontSize={14}
        useThemeColours={noBg}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.25,
          color: noBg ? "text.primary" : "rgba(255,255,255,0.8)",
        }}
      >
        {formatNumber(Math.abs(change))}% vs previous period
      </Typography>
    </Stack>
  );
}

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  return (
    <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
      <Alert severity="error" onClose={resetErrorBoundary}>
        {error.message}
      </Alert>
    </Box>
  );
};

export interface IAnalyticsChartStatCardProps {
  chart: IChart;
  fullHeight?: boolean;
  onEdit?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  noToolbar?: boolean;
  topDimension?: ApiDimension;
  chartOutput?: IFromToChartOutput;
  overrideComparisonOutput?: IFromToChartOutput;
  /** When set, skips the analytics fetch and displays this value instead. */
  overrideValue?: number;
  overrideComparisonValue?: number;
  hideTrendedSpark?: boolean;
  forceComparison?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

function AnalyticsChartStatCard({
  chart,
  fullHeight,
  onEdit,
  onDelete,
  noToolbar,
  topDimension,
  chartOutput,
  overrideComparisonOutput,
  overrideValue,
  overrideComparisonValue,
  hideTrendedSpark,
  forceComparison,
  loading,
  icon,
}: IAnalyticsChartStatCardProps) {
  const metricToName = useMetricName();
  const { chartComparisonOutput } = useFromToContext();
  const formatMetric = useFormatMetric();
  const [showToolBar, setShowToolBar] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { model } = useAttributionContext();
  const metrics = useMemo(() => {
    const yAxisKeys = replaceAttributionMetricsInChart(chart, [
      model || ApiAttributionModel.EBA,
    ]).yAxisKeys;
    const fields =
      yAxisKeys.length > 0 ? (yAxisKeys[0] as ApiChartYAxisKey).fields : null;
    if (!fields) {
      return [];
    }
    return fields as ApiMetric[];
  }, [chart, model]);

  const noBg = useMemo(
    () => !!chart.displayOptions?.statNoBg,
    [chart.displayOptions?.statNoBg],
  );

  const hasCompare = useMemo(
    () => Boolean(chartComparisonOutput) || forceComparison,
    [chartComparisonOutput, forceComparison],
  );

  const overrideData = useMemo((): TChartData[] | undefined => {
    if (overrideValue === undefined || metrics.length === 0) {
      return undefined;
    }
    const row: TChartData = { name: "" };
    metrics.forEach((metric, index) => {
      if (index === 0) {
        row[metric] = overrideValue;
      }
    });
    return [row];
  }, [overrideValue, metrics]);

  const overrideComparisonData = useMemo((): TChartData[] | undefined => {
    if (overrideComparisonValue === undefined || metrics.length === 0) {
      return undefined;
    }
    return [{ name: "", [metrics[0]]: overrideComparisonValue }];
  }, [overrideComparisonValue, metrics]);

  return (
    <ErrorBoundary
      fallback={({ error, resetErrorBoundary }) => (
        <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    >
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{ position: "relative", width: "100%" }}
      >
        <AnalyticsStatCard
          icon={icon}
          forceComparison={forceComparison}
          chartOutput={chartOutput}
          overrideComparisonOutput={overrideComparisonOutput}
          overrideData={overrideData}
          overrideComparisonData={overrideComparisonData}
          loading={loading}
          fullHeight={fullHeight}
          minHeight={
            metrics.length === 1 &&
            overrideValue === undefined &&
            !hideTrendedSpark
              ? 175
              : undefined
          }
          chart={chart}
          includeTrendedSpark={
            overrideValue !== undefined || hideTrendedSpark
              ? false
              : metrics.length === 1
          }
          noBg={noBg}
          renderBody={(data, comparisonData, trendedChartData) => {
            const changeOne = percentageTopChangeByMetric(
              data,
              comparisonData || [],
              metrics[0],
            );

            const changeTwo = percentageTopChangeByMetric(
              data,
              comparisonData || [],
              metrics[1],
            );

            const trendPoints = trendedChartData
              ? trendedChartData.map((t) => Number(t[metrics[0]]))
              : [];
            const showSparkline = hasSparklineData(trendPoints);

            return metrics.length > 1 ? (
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Stack spacing={0.75}>
                  <StatCardTypography
                    size="xs"
                    color={noBg ? "text.primary" : undefined}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {chart.displayOptions?.statTitle ||
                      metricToName(metrics[0]!)}
                  </StatCardTypography>
                  <Stack
                    direction="row"
                    alignItems="flex-end"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    <StatCardTypography
                      size="medium"
                      color={noBg ? "primary" : undefined}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {formatMetric(metrics[0], Number(data[0][metrics[0]!]))}
                    </StatCardTypography>
                    {hasCompare && typeof changeOne !== "undefined" && (
                      <Box pb={0.5}>
                        <StatCardComparison change={changeOne} noBg={noBg} />
                      </Box>
                    )}
                  </Stack>
                </Stack>
                <Stack spacing={0.75}>
                  <StatCardTypography
                    size="xs"
                    color={noBg ? "text.primary" : undefined}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {chart.displayOptions?.statTitle2 ||
                      metricToName(metrics[1]!)}
                  </StatCardTypography>
                  <Stack
                    direction="row"
                    alignItems="flex-end"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    <StatCardTypography
                      size="medium"
                      color={noBg ? "primary" : undefined}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {formatMetric(metrics[1], Number(data[0][metrics[1]!]))}
                    </StatCardTypography>
                    {hasCompare && typeof changeTwo !== "undefined" && (
                      <Box pb={0.5}>
                        <StatCardComparison change={changeTwo} noBg={noBg} />
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            ) : (
              <Stack
                sx={{
                  position: "relative",
                  flex: 1,
                  minWidth: 0,
                  pb: showSparkline ? 4 : 0,
                }}
              >
                {!topDimension ? (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    mb={2.5}
                  >
                    <StatCardTypography
                      size="xs"
                      color={noBg ? "text.primary" : undefined}
                      sx={{ wordBreak: "break-word", pr: 1 }}
                    >
                      {chart.displayOptions?.statTitle ||
                        metricToName(metrics[0]!)}
                    </StatCardTypography>
                  </Stack>
                ) : (
                  <StatCardTypography
                    size="xs"
                    color={noBg ? "text.primary" : undefined}
                    mb={1}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {chart.displayOptions?.statTitle ||
                      metricToName(metrics[0]!)}
                  </StatCardTypography>
                )}

                <StatCardTypography
                  size={topDimension ? "medium" : "large"}
                  color={noBg ? "primary" : undefined}
                  sx={{
                    wordBreak: "break-word",
                    mb: hasCompare && typeof changeOne !== "undefined" ? 1 : 0,
                    lineHeight: 1.1,
                  }}
                >
                  {topDimension
                    ? `${data?.[0]?.[topDimension]}`
                    : formatMetric(
                        metrics[0],
                        Number(data?.[0]?.[metrics[0]] || 0),
                        { isKM: true },
                      )}
                </StatCardTypography>

                {hasCompare && typeof changeOne !== "undefined" && (
                  <StatCardComparison change={changeOne} noBg={noBg} />
                )}

                {showSparkline && (
                  <StatCardSparkline trendPoints={trendPoints} noBg={noBg} />
                )}
              </Stack>
            );
          }}
        />
        {!noToolbar && (
          <>
            <AnimatePresence mode="wait">
              {showToolBar ? (
                <AnalyticsCardToolBar
                  setOpen={setShowToolBar}
                  chart={chart}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ) : (
                isHovered && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        padding: 0.2,
                        border: `1px solid white`,
                      }}
                      onClick={() => setShowToolBar(true)}
                    >
                      <MoreHoriz sx={{ fontSize: 24, fill: "white" }} />
                    </IconButton>
                  </Box>
                )
              )}
            </AnimatePresence>
          </>
        )}
      </Box>
    </ErrorBoundary>
  );
}

export default AnalyticsChartStatCard;
